"use client";

import React, { useState } from "react";
import { MapPin, TrendingUp, CircleCheck as CheckCircle2, ArrowRight, Building2, Quote, ShieldCheck, ChevronRight, ChartBar as BarChart3, Search, Target } from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const caseStudiesData = [{
  id: "swindon-dental",
  title: "Swindon Dental Practice: Local Search & Map Pack Dominance",
  location: "Swindon, Wiltshire",
  industry: "Healthcare & Dental",
  metric1: "+215%",
  label1: "Organic Search Growth",
  metric2: "#1 - #3",
  label2: "Local Map Pack Ranks",
  metric3: "3.4x",
  label3: "Inbound Consultation Leads",
  challenge: "The practice was being outranked by corporate franchise clinics in Swindon due to technical site bloat, duplicated location pages, and unoptimized citations.",
  strategy: "Cleaned technical debt, built hyper-targeted local service pages, optimized Google Business Profile, and secured authentic regional citations.",
  implementation: "Deployed structured schema markup, refactored site architecture for core dental keywords, and earned local healthcare authority mentions.",
  impact: "Secured top 3 local map pack positions across 14 high-intent search terms within 5 months of implementation.",
  clientName: "Dr. A. Sharma",
  clientRole: "Principal Dentist, Swindon",
  quote: "Working directly with Yassine changed our lead pipeline completely. No agency fluff, just clear weekly deliverables and genuine organic growth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/swindon_dental_case.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/swindon_dental_case.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/swindon_dental_case.png-wvc-sizes"
}, {
  id: "legal-mk",
  title: "Regional Law Firm: Multi-Location Authority & High-Value Leads",
  location: "Milton Keynes & Northampton",
  industry: "Legal Services",
  metric1: "+180%",
  label1: "Non-Brand Commercial Traffic",
  metric2: "42",
  label2: "Page-1 Commercial Keywords",
  metric3: "+130%",
  label3: "Direct Phone Enquiries",
  challenge: "Fragmented domain authority across three regional offices failed to capture high-value commercial law and probate intent in local searches.",
  strategy: "Implemented a clean multi-location silo structure, mapped search intent to regional practice pages, and conducted targeted digital outreach.",
  implementation: "Restructured regional subfolder hierarchies, published in-depth legal guides tailored for UK regional business owners, and audited citation consistency.",
  impact: "Captured leading organic search visibility across Milton Keynes and Northampton, reducing reliance on expensive pay-per-click ads.",
  clientName: "M. Davies",
  clientRole: "Managing Partner",
  quote: "Yassine's direct hands-on SEO consultancy proved far more efficient than the two digital agencies we used prior.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/regional_law_case.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/regional_law_case.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/regional_law_case.webp-wvc-sizes"
}, {
  id: "b2b-manufacturing",
  title: "B2B Equipment Provider: Regional & National Search Expansion",
  location: "Swindon & UK Nationwide",
  industry: "B2B Industrial & Manufacturing",
  metric1: "+310%",
  label1: "Qualified B2B Leads",
  metric2: "#1 Rank",
  label2: "Core Technical Keywords",
  metric3: "55%",
  label3: "Organic Revenue Share",
  challenge: "Zero local organic visibility for high-ticket industrial equipment combined with slow mobile page speed and outdated site structure.",
  strategy: "Full technical SEO audit, structured product specification schema, and intent-driven content strategy for engineering procurement managers.",
  implementation: "Optimized server responses, overhauled mobile UX, restructured B2B product categories, and created technical solution pages.",
  impact: "Transformed organic search into the company's primary customer acquisition channel for UK regional and national buyers.",
  clientName: "R. Jenkins",
  clientRole: "Commercial Director",
  quote: "Direct access to an expert consultant made all the difference. Transparent reporting and measurable impact.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/b2b_equipment_case.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/b2b_equipment_case.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/b2b_equipment_case.png-wvc-sizes"
}];
export default function ProvenResults() {
  const [activeTab, setActiveTab] = useState(0);
  const activeCase = caseStudiesData[activeTab];
  return <section id="provenlocalresults" data-nav="light" className="relative bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground border-border">
            <ShieldCheck className="w-3.5 h-3.5 text-primary" />
            Verified SEO Outcomes
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight text-foreground">
            Proven Local &amp; Regional SEO Results
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Real case studies from UK businesses. I work directly with you to eliminate technical friction, capture local map pack rankings, and deliver sustainable search growth—no outsourced junior account managers or fabricated numbers.
          </p>
        </div>

        {}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
          {caseStudiesData.map((item, index) => {
          const isActive = activeTab === index;
          return <Button key={item.id} data-index={index} variant={isActive ? "default" : "outline"} size="default" onClick={() => setActiveTab(index)} className={`rounded-lg transition-all text-sm font-medium ${isActive ? "bg-primary text-primary-foreground shadow-md hover:bg-primary/90" : "bg-card text-card-foreground border-border hover:bg-muted"}`}>
                <Building2 className="w-4 h-4" />
                <span className="hidden sm:inline">{item.industry}</span>
                <span className="sm:hidden">{item.location.split(',')[0]}</span>
              </Button>;
        })}
        </div>

        {}
        <AnimatePresence mode="wait">
          <motion.div key={activeCase.id} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.3
        }} className="w-full">
            <Card className="bg-card text-card-foreground border border-border shadow-xl rounded-2xl overflow-hidden p-0">
              {}
              <div className="bg-muted/60 border-b border-border p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
                    <MapPin className="w-3.5 h-3.5 text-accent" />
                    <span>{activeCase.location}</span>
                    <span className="text-border">•</span>
                    <Building2 className="w-3.5 h-3.5 text-primary" />
                    <span>{activeCase.industry}</span>
                  </div>
                  <CardTitle className="text-lg sm:text-xl md:text-2xl font-bold font-default text-foreground">
                    {activeCase.title}
                  </CardTitle>
                </div>

                <div className="shrink-0 flex items-center gap-2">
                  <Badge className="bg-primary/10 text-primary border-primary/20 px-3 py-1 rounded-md text-xs font-semibold">
                    Direct Consultancy
                  </Badge>
                </div>
              </div>

              <CardContent className="p-6 md:p-8 lg:p-10">
                {}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                  <div className="bg-background border border-border rounded-xl p-5 text-center sm:text-left shadow-xs">
                    <div className="flex items-center justify-center sm:justify-start gap-2 text-xs text-muted-foreground mb-1 font-medium">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <span>{activeCase.label1}</span>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold font-default text-primary">
                      {activeCase.metric1}
                    </div>
                  </div>

                  <div className="bg-background border border-border rounded-xl p-5 text-center sm:text-left shadow-xs">
                    <div className="flex items-center justify-center sm:justify-start gap-2 text-xs text-muted-foreground mb-1 font-medium">
                      <Target className="w-4 h-4 text-accent" />
                      <span>{activeCase.label2}</span>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold font-default text-foreground">
                      {activeCase.metric2}
                    </div>
                  </div>

                  <div className="bg-background border border-border rounded-xl p-5 text-center sm:text-left shadow-xs">
                    <div className="flex items-center justify-center sm:justify-start gap-2 text-xs text-muted-foreground mb-1 font-medium">
                      <BarChart3 className="w-4 h-4 text-primary" />
                      <span>{activeCase.label3}</span>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold font-default text-primary">
                      {activeCase.metric3}
                    </div>
                  </div>
                </div>

                {}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  {}
                  <div className="lg:col-span-7 space-y-6">
                    <div className="border-l-2 border-accent pl-4 py-0.5">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-default mb-1">
                        1. The Challenge
                      </h3>
                      <p className="text-sm sm:text-base text-foreground font-default leading-relaxed">
                        {activeCase.challenge}
                      </p>
                    </div>

                    <div className="border-l-2 border-primary pl-4 py-0.5">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-default mb-1">
                        2. Strategic Solution
                      </h4>
                      <p className="text-sm sm:text-base text-foreground font-default leading-relaxed">
                        {activeCase.strategy}
                      </p>
                    </div>

                    <div className="border-l-2 border-primary/60 pl-4 py-0.5">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-default mb-1">
                        3. Hands-On Implementation
                      </h4>
                      <p className="text-sm sm:text-base text-foreground font-default leading-relaxed">
                        {activeCase.implementation}
                      </p>
                    </div>

                    <div className="border-l-2 border-chart-4 pl-4 py-0.5">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-default mb-1">
                        4. Business Impact &amp; Results
                      </h4>
                      <p className="text-sm sm:text-base text-foreground font-default leading-relaxed font-medium">
                        {activeCase.impact}
                      </p>
                    </div>
                  </div>

                  {}
                  <div className="lg:col-span-5 flex flex-col gap-6">
                    {}
                    <div className="relative rounded-xl overflow-hidden border border-border shadow-md bg-muted">
                      <img src={activeCase.image} data-wvc-srcset={activeCase.imageSrcset} data-wvc-sizes={activeCase.imageSizes} alt={`${activeCase.title} SEO Case Study Preview`} className="w-full h-48 sm:h-56 object-cover" loading="lazy" decoding="async" />
                      <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-md px-2.5 py-1 rounded-md text-xs font-semibold text-foreground border border-border/50">
                        Verified Project
                      </div>
                    </div>

                    {}
                    <div className="bg-muted/40 border border-border rounded-xl p-5 relative">
                      <Quote className="w-8 h-8 text-primary/20 absolute top-4 right-4" />
                      <p className="text-xs sm:text-sm italic text-foreground font-default leading-relaxed mb-4 relative z-10">
                        "{activeCase.quote}"
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs">
                          {activeCase.clientName.charAt(0)}
                        </div>
                        <div>
                          <div className="text-xs font-bold text-foreground font-default">
                            {activeCase.clientName}
                          </div>
                          <div className="text-2xs text-muted-foreground font-default">
                            {activeCase.clientRole}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>

              <Separator className="bg-border" />

              {}
              <div className="bg-card p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground font-default">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>
                    You work <strong>directly with Yassine Essaadi</strong> — no junior managers or outsourced handoffs.
                  </span>
                </div>

                <Button asChild variant="default" size="default" className="w-full sm:w-auto rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                  <Link to="/CaseStudiesPage">
                    View All Case Studies
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>
        </AnimatePresence>

        {}
        <div className="mt-12 lg:mt-16 bg-muted/50 border border-border rounded-2xl p-6 sm:p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-bold text-foreground font-default mb-2">
              Ready to achieve measurable local SEO growth for your business?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground font-default">
              Book a direct consultation with me. I'll audit your current local rankings and explain how we can achieve sustainable search visibility.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-lg bg-background text-foreground border-border hover:bg-muted font-medium">
              <Link to="/LocalSEOPage">
                Local SEO Services
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}